import { FC, Ref, useContext, useRef } from "react";

import { HoverableToolbarContext } from "components/hover-widgets/HoverableToolbarProvider";
import { AddCourseModal } from "components/materials/home/admin/content/courses/AddCourseModal";
import { MoveCourseCodesToGroupingModal } from "components/materials/home/admin/content/courses/MoveCoursesToGroupingModal";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { Menu, type MenuChangeEvent, MenuInstance, MenuItem } from "mds/components/Menu";
import { showConfirmationModal } from "mds/components/Modal";
import { AddPlusIcon, ChevronDownIcon, CopySendArrowIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectCoursesWithGrouping } from "store/selectors/section";

type AddCourseOptionMenuProps = {
  groupingId: string;
  localCourses: Array<{ order?: number }>;
  eventPrefix?: string;
  className?: string;
};

export const AddCourseOptionMenu: FC<AddCourseOptionMenuProps> = ({
  groupingId,
  localCourses,
  eventPrefix,
  className,
}) => {
  const menuRef: Ref<MenuInstance> = useRef(null);
  const coursesWithGrouping = useAppSelector(selectCoursesWithGrouping);
  const hoverableContext = useContext(HoverableToolbarContext);

  const onMenuChange = (event: MenuChangeEvent) => {
    hoverableContext?.setShouldStayOpen?.(event.open);
  };

  const handleAddNewCourseCode = () => {
    showConfirmationModal({
      title: t("home.courses.add_course_title"),
      hideCancelConfirmButtons: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      children: (onClose) => (
        <AddCourseModal
          eventName={eventPrefix ? `${eventPrefix}Add Course Code` : undefined}
          localCourses={localCourses}
          onClose={onClose}
        />
      ),
    });
    menuRef.current?.closeMenu();
  };

  const handleMoveExistingCourseCodes = () => {
    showConfirmationModal({
      title: t("home.courses.move_courses_to_grouping_title"),
      hideCancelConfirmButtons: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      children: (onClose) => (
        <MoveCourseCodesToGroupingModal
          courses={coursesWithGrouping}
          groupingId={groupingId}
          onClose={onClose}
        />
      ),
    });

    menuRef.current?.closeMenu();
  };

  const MenuButton = (
    <Button
      className={className}
      eventName={eventPrefix}
      kind="gray"
      size="s"
      title={t("common.add.course")}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <IconText iconEnd={<ChevronDownIcon />} text={t("common.add.course")} />
    </Button>
  );

  return (
    <Menu
      instanceRef={menuRef}
      menuButton={MenuButton}
      menuClassName="min-w-[180px]"
      onMenuChange={onMenuChange}
    >
      <MenuItem onClick={handleAddNewCourseCode}>
        <IconText iconStart={<AddPlusIcon />} text={t("sidebar_options_menu.add_new_course")} />
      </MenuItem>

      <MenuItem onClick={handleMoveExistingCourseCodes}>
        <IconText
          iconStart={<CopySendArrowIcon />}
          text={t("sidebar_options_menu.move_existing_courses_here")}
        />
      </MenuItem>
    </Menu>
  );
};
